import React, { Component } from 'react';
import styles from './dropdown.styl';
import classNames from 'classnames';

// modify event property instead of using context to determine the Item click
// this would allow to stay compatible with Preact (React context is not compatible)
interface DropdownItemMouseEvent extends React.MouseEvent<HTMLDivElement> {
  fromDropdownItem: true;
}

interface DropdownItemProps {
  link?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}

export class DropdownItem extends Component<DropdownItemProps> {
  onItemClick = (event: DropdownItemMouseEvent) => {
    if (this.props.disabled) {
      return;
    }

    event.fromDropdownItem = true;
    if (this.props.onClick) {
      this.props.onClick();
    }
  };

  render() {
    const { link, disabled, children } = this.props;

    return (
      <div
        className={classNames(styles.item, {
          [styles.isLink]: link,
          [styles.isDisabled]: disabled,
        })}
        onClick={this.onItemClick}
      >
        {children}
      </div>
    );
  }
}

type DropdownCloseSource = 'outside' | 'item' | 'trigger' | 'keyboard';
type DropdownPosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

interface DropdownProps {
  isOpen: boolean;
  trigger: JSX.Element;
  position: DropdownPosition;

  closeOnEscape?: boolean;
  closeOnOutsideClick?: boolean;
  closeOnItemClick?: boolean;

  onRequestClose: (source: DropdownCloseSource) => void;
  onRequestOpen: () => void;
}

export class Dropdown extends Component<DropdownProps> {
  static defaultProps = {
    closeOnEscape: true,
    closeOnItemClick: true,
    closeOnOutsideClick: true,
  };

  private insideClicked = false;

  private onBodyClick = () => {
    if (!this.props.isOpen) {
      return;
    }

    if (this.insideClicked) {
      return;
    }

    if (this.props.closeOnOutsideClick) {
      this.props.onRequestClose('outside');
    }
  };

  private onKeydown = (event: KeyboardEvent) => {
    if (!this.props.isOpen) {
      return;
    }

    if (!this.props.closeOnEscape) {
      return;
    }

    const ESCAPE_CODE = 27;
    const code = event.which;
    if (code === ESCAPE_CODE) {
      this.props.onRequestClose('keyboard');
    }
  };

  componentDidMount() {
    document.addEventListener('click', this.onBodyClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.onBodyClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  // mark event as click inside to be handled on body click after the propagation
  // cleanup is happening after the current "tick" - event is propagated to the BODY
  // on the same tick
  private insideClick() {
    this.insideClicked = true;
    window.setTimeout(() => {
      this.insideClicked = false;
    }, 0);
  }

  private onTriggerClick = () => {
    this.insideClick();

    if (this.props.isOpen) {
      this.props.onRequestClose('trigger');
      return;
    }

    this.props.onRequestOpen();
  };

  private onContentClick = (event: DropdownItemMouseEvent) => {
    this.insideClick();

    if (event.fromDropdownItem) {
      this.props.onRequestClose('item');
    }
  };

  render() {
    const { isOpen, children, trigger, position } = this.props;
    return (
      <div
        className={classNames(styles.root, {
          [styles.isOpen]: isOpen,
          [styles.top]: position === 'top-left' || position === 'top-right',
          [styles.bottom]: position === 'bottom-left' || position === 'bottom-right',
          [styles.left]: position === 'bottom-left' || position === 'top-left',
          [styles.right]: position === 'bottom-right' || position === 'top-right',
        })}
      >
        <div className={styles.trigger} onClick={this.onTriggerClick}>
          {trigger}
        </div>
        <div className={styles.content} onClick={this.onContentClick}>
          {children}
        </div>
      </div>
    );
  }
}
